import React, { useState } from 'react'
import { ProAccountChatListContainer } from 'containers'
import { makeStyles } from '@material-ui/core/styles'
import { Chat, OnlineIndicator, Typography, Tooltip } from 'components'
import { colors } from 'constants/index'
import { useSelector } from 'react-redux'
import classNames from 'classnames'
import { ReactComponent as ShareIcon } from 'static/cases/share.svg'
import SharedClientProjectsModal from 'components/connections/SharedClientProjectsModal'

const useStyles = makeStyles({
  container: {
    display: 'flex',
    height: '100%',
  },
  chat: {
    borderTop: `1px solid ${colors.BASIC.COLOR['10']}`,
    height: '100%',
    overflowY: 'auto',
  },
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '70%',
    height: '100%',
    borderLeft: `1px solid ${colors.BASIC.COLOR['10']}`,
    borderRight: `1px solid ${colors.BASIC.COLOR['10']}`,
  },
  title: {
    margin: '11px 5px 11px 24px',
  },
  titleContainer: {
    height: 59,
    display: 'flex',
    alignItems: 'center',
  },
  connectionList: {
    width: '30%',
  },
  indicator: {
    marginLeft: 6,
    fontSize: 14,
  },
  transparent: {
    opacity: 0,
  },
  shareButton: {
    width: 16,
    height: 16,
    marginLeft: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    cursor: 'pointer',
  },
  shareButtonRotated: {
    transform: 'rotate(180deg)',
  },
  tooltip: {
    backgroundColor: colors.BASIC.COLOR[80],
    color: colors.WHITE,
    fontSize: 13,
    fontWeight: 'normal',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
})

const ProAccountChatContainer = () => {
  const classes = useStyles()
  const selectedChat = useSelector(state => state.chat.selectedChat)
  const chatsType = useSelector(state => state.chat.chatsType)
  const [showSharedProjectsModal, setShowSharedProjectsModal] = useState(false)

  // Determine if chat is shared (for clients)
  const isSharedByMe =
    chatsType === 'client' && (selectedChat.shared_cases_count > 0 || selectedChat.shared_with_count > 0)
  const isSharedWithMe = chatsType === 'client' && !!selectedChat.shared_by_full_name?.length

  // Determine if chat is shared (for colleagues)
  const sharedCases = chatsType === 'colleague' ? selectedChat.shared_case_names || [] : []
  const hasSharedCases = sharedCases.length > 0

  const getShareTooltip = () => {
    if (chatsType === 'client') {
      if (isSharedByMe) {
        const count = selectedChat.shared_cases_count || selectedChat.shared_with_count || 0
        return `${count} project${count !== 1 ? 's' : ''} shared with other providers for this client.`
      } else if (isSharedWithMe && selectedChat.shared_by_full_name) {
        return `This client's projects shared with you by ${selectedChat.shared_by_full_name}`
      }
    } else if (chatsType === 'colleague' && hasSharedCases) {
      const caseNames = sharedCases.join(', ')
      return `Added as part of ${caseNames} project${sharedCases.length !== 1 ? 's' : ''}`
    }
    return ''
  }

  const showShareIcon =
    (chatsType === 'client' && (isSharedByMe || isSharedWithMe)) || (chatsType === 'colleague' && hasSharedCases)

  const handleShareIconClick = e => {
    e.stopPropagation()
    if (chatsType === 'client') {
      setShowSharedProjectsModal(true)
    }
  }

  return (
    <div className={classes.container}>
      <div className={classes.chatContainer}>
        <div className={classes.titleContainer}>
          <div className={classes.titleWrapper}>
            <Typography variant="h4" className={classes.title}>
              {selectedChat.chat_member_full_name}
            </Typography>
            {showShareIcon && (
              <Tooltip tooltipClass={classes.tooltip} title={getShareTooltip()} placement="bottom-start">
                <ShareIcon
                  className={`${classes.shareButton} ${isSharedWithMe ? classes.shareButtonRotated : ''}`}
                  onClick={handleShareIconClick}
                />
              </Tooltip>
            )}
          </div>
          <OnlineIndicator
            className={classNames({
              [classes.indicator]: true,
              [classes.transparent]: !selectedChat.chat_member_is_online,
            })}
            showText
          />
        </div>
        <div className={classes.chat}>
          <Chat selectedChat={selectedChat} />
        </div>
      </div>
      <div className={classes.connectionList}>
        <ProAccountChatListContainer selectedChatId={selectedChat.connection_uuid} />
      </div>
      {chatsType === 'client' && (
        <SharedClientProjectsModal
          isOpen={showSharedProjectsModal}
          onClose={() => setShowSharedProjectsModal(false)}
          clientUuid={selectedChat.client_uuid}
          clientName={selectedChat.chat_member_full_name}
          type={isSharedByMe ? 'shared_by_me' : 'shared_with_me'}
          proUuid={isSharedWithMe ? selectedChat.main_pro_uuid : undefined}
        />
      )}
    </div>
  )
}

export default ProAccountChatContainer
